import React, { useState } from 'react';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  SalaryHistoryDbType,
  SalaryItemRate,
} from '../../../../../api/types/accountants/salary-history/salary-history.type';
import { LegalEntityType } from '../../../../../pages/user-page/roles/lecturer-price-rate/componenets/acountant/user-page-lecturer-rate-accountant';
import dateFormat from '../../../../../utils/date-format';

interface MySalaryTableProps {
  salariesData: SalaryHistoryDbType[];
}

export const MySalaryTable: React.FC<MySalaryTableProps> = ({
  salariesData,
}) => {
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('he-IL').format(new Date(date));
  };

  const totalAmountTemplate = (rowData: SalaryHistoryDbType) => {
    const total = rowData.items.reduce((sum, item) => sum + item.rate, 0);
    return formatCurrency(total);
  };

  const itemsCountTemplate = (rowData: SalaryHistoryDbType) => {
    return rowData.items.length;
  };

  // Helper function to get unique legal entity types for a user
  const getLegalEntityTypesTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueTypes = [
      ...new Set(rowData.items.map((item) => item.legalEntityType)),
    ];
    if (uniqueTypes.length === 1) {
      return LegalEntityType.getDisplayText(uniqueTypes[0]);
    }
    return uniqueTypes
      .map((type) => LegalEntityType.getDisplayText(type))
      .join(', ');
  };

  // Helper function to get unique reporting codes for a user
  const getReportingCodesTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueCodes = [
      ...new Set(rowData.items.map((item) => item.reportingCode)),
    ];
    return uniqueCodes.join(', ');
  };

  // Helper function to get unique legal entity IDs for a user
  const getLegalEntityIdsTemplate = (rowData: SalaryHistoryDbType) => {
    const uniqueIds = [
      ...new Set(rowData.items.map((item) => item.legalEntityId)),
    ];
    return uniqueIds.join(', ');
  };

  const expandedRowTemplate = (data: SalaryHistoryDbType) => {
    return (
      <div>
        <h5 style={{ display: 'flex', justifyContent: 'right' }}>
          פירוט תשלומים
        </h5>
        <DataTable value={data.items} responsiveLayout="scroll" size="small">
          <Column field="id" header="מזהה" style={{ textAlign: 'right' }} />
          <Column
            field="rate"
            header="תעריף"
            body={(rowData: SalaryItemRate) => formatCurrency(rowData.rate)}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="rate"
            header="כמות"
            body={(rowData: SalaryItemRate) => {
              const hours =
                (new Date(rowData.endAt).getTime() -
                  new Date(rowData.startAt).getTime()) /
                (1000 * 60 * 60);
              const formatted =
                hours % 1 === 0 ? hours.toString() : hours.toFixed(1);
              return <label>{formatted} שעות</label>;
            }}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="activityType"
            header="סוג פעילות"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="legalEntityType"
            header="סוג ישות משפטית"
            style={{ textAlign: 'right' }}
            body={(rowData: SalaryItemRate) => (
              <div>
                {LegalEntityType.getDisplayText(rowData.legalEntityType)}
              </div>
            )}
          />
          <Column
            field="reportingCode"
            header="קוד דיווח במערכת שכר"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="legalEntityId"
            header="מזהה ישות משפטית"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="createdByName"
            header="מפיק משכורת"
            style={{ textAlign: 'right' }}
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            body={(rowData: SalaryItemRate) =>
              dateFormat.displayFormatDate(rowData.createdAt)
            }
            style={{ textAlign: 'right' }}
          />
        </DataTable>
      </div>
    );
  };

  const expanderTemplate = (rowData: SalaryHistoryDbType) => {
    const isExpanded = expandedRows[rowData.id] !== undefined;
    return (
      <button
        type="button"
        onClick={() => {
          const newExpandedRows = { ...expandedRows };
          if (isExpanded) {
            delete newExpandedRows[rowData.id];
          } else {
            newExpandedRows[rowData.id] = true;
          }
          setExpandedRows(newExpandedRows);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isExpanded ? '▼' : '▶'}
      </button>
    );
  };

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
      }}
    >
      <div
        style={{
          padding: '20px',
          borderBottom: '1px solid #E5E7EB',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#041E49',
        }}
      >
        היסטוריית שכר
      </div>

      <div style={{ padding: '20px' }}>
        <DataTable
          value={salariesData}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
          responsiveLayout="scroll"
          dataKey="id"
          rowExpansionTemplate={expandedRowTemplate}
          tableStyle={{ minWidth: '50rem', direction: 'rtl' }}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתוני שכר</div>
          }
          className="p-datatable-striped"
        >
          <Column
            expander={false}
            body={expanderTemplate}
            style={{ width: '3rem', textAlign: 'center' }}
          />

          <Column
            header="סוג ישות משפטית"
            body={getLegalEntityTypesTemplate}
            sortable
            style={{ textAlign: 'right', minWidth: '150px' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            header="קוד דיווח"
            body={getReportingCodesTemplate}
            sortable
            style={{ textAlign: 'right', minWidth: '120px' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            header="מזהה ישות משפטית"
            body={getLegalEntityIdsTemplate}
            sortable
            style={{ textAlign: 'right', minWidth: '150px' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            header="מספר תשלומים"
            body={itemsCountTemplate}
            sortable
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            header="סכום כולל"
            body={totalAmountTemplate}
            sortable
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />

          <Column
            field="createdAt"
            header="תאריך יצירה"
            body={(rowData: SalaryHistoryDbType) =>
              formatDate(rowData.createdAt)
            }
            sortable
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
        </DataTable>
      </div>
    </div>
  );
};
